import { AlertTriangle, RefreshCw } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

// Error types from backend (must match ByocErrorReason in byoc.dto.ts)
export type ByocErrorReason =
  | 'oauth_refresh_failed'
  | 'api_key_invalid'
  | 'spending_cap_reached'
  | 'network_error'
  | 'decryption_failed';

interface ByocExpiredModalProps {
  open: boolean;
  errorType: ByocErrorReason | null;
  title?: string;
  message?: string;
  onReconnect: () => void;
  onContinue: () => void;
}

// Default messages if not provided by backend
// User has already been disconnected and switched to workspace credits
const DEFAULT_MESSAGES: Record<ByocErrorReason, { title: string; message: string }> = {
  oauth_refresh_failed: {
    title: 'Claude Session Expired',
    message: 'Your Claude session expired. You\'ve been switched to workspace credits.',
  },
  api_key_invalid: {
    title: 'API Key Invalid',
    message: 'Your API key is invalid or revoked. You\'ve been switched to workspace credits.',
  },
  spending_cap_reached: {
    title: 'Spending Cap Reached',
    message: 'Your Claude spending cap has been reached. You\'ve been switched to workspace credits.',
  },
  network_error: {
    title: 'Connection Issue',
    message: 'Unable to verify your Claude connection. You\'ve been switched to workspace credits.',
  },
  decryption_failed: {
    title: 'Connection Error',
    message: 'Error reading your credentials. You\'ve been switched to workspace credits.',
  },
};

export function ByocExpiredModal({
  open,
  errorType,
  title,
  message,
  onReconnect,
  onContinue,
}: ByocExpiredModalProps) {
  // Use provided title/message or fall back to defaults
  const defaultContent = errorType ? DEFAULT_MESSAGES[errorType] : DEFAULT_MESSAGES.oauth_refresh_failed;
  const displayTitle = title || defaultContent.title;
  const displayMessage = message || defaultContent.message;

  // Hide reconnect for spending cap - reconnecting won't help until cap resets
  const showReconnect = errorType !== 'spending_cap_reached';

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-[360px] p-5"
        hideCloseButton
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="space-y-2">
          <div className="flex items-center gap-2.5">
            <div className="rounded-full bg-amber-100 dark:bg-amber-900/30 p-1.5">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
            <DialogTitle className="text-base">{displayTitle}</DialogTitle>
          </div>
          <DialogDescription className="text-sm space-y-2">
            <p>{displayMessage}</p>
            {showReconnect && <p>To continue using your Claude account, reconnect below.</p>}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 mt-3">
          <Button
            variant={showReconnect ? 'outline' : 'default'}
            size="sm"
            onClick={onContinue}
            className="flex-1"
          >
            Continue
          </Button>
          {showReconnect && (
            <Button
              size="sm"
              onClick={onReconnect}
              className="flex-1"
            >
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              Reconnect
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
